import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import Pagination from "@mui/material/Pagination";
import { ArrowDownTrayIcon } from "@heroicons/react/24/outline";
import { Breadcrumbs, Pill } from "@truevault/aquilo";
import { StandardPageWrapper } from "../../../components/layout/StandardPageWrapper";
import { Routes } from "../../../root/routes";
import { usePrimaryOrganizationId } from "../../../hooks";
import { ApiRouteHelpers } from "../../../../common/root/apiRoutes";

type ExportJob = {
  jobId: string;
  exportType: "CONSENT_RECORDS" | "GPC_OPT_OUTS" | "RECORD_LOOKUP";
  status: "PENDING" | "RUNNING" | "SUCCEEDED" | "FAILED" | "EXPIRED";
  requestedAt: string;
  fromDate?: string;
  toDate?: string;
  downloadUrl?: string | null;
};

const PAGE_SIZE = 15;

const STATUS_PILL_VARIANT: Record<
  ExportJob["status"],
  "info" | "default" | "completed" | "error" | "warning"
> = {
  PENDING: "default",
  RUNNING: "info",
  SUCCEEDED: "completed",
  FAILED: "error",
  EXPIRED: "warning",
};

const STATUS_LABEL: Record<ExportJob["status"], string> = {
  PENDING: "Pending",
  RUNNING: "Processing",
  SUCCEEDED: "Complete",
  FAILED: "Failed",
  EXPIRED: "Expired",
};

const EXPORT_TYPE_LABEL: Record<ExportJob["exportType"], string> = {
  CONSENT_RECORDS: "Consent Records",
  GPC_OPT_OUTS: "GPC Opt-Outs",
  RECORD_LOOKUP: "Record Lookup",
};

const formatDateLocal = (iso?: string) => {
  if (!iso) return "—";
  const parsed = new Date(iso);
  if (Number.isNaN(parsed.getTime())) return "—";
  return parsed.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
};

const formatDateUtc = (iso?: string) => {
  if (!iso) return "—";
  const parsed = new Date(iso);
  if (Number.isNaN(parsed.getTime())) return "—";
  return parsed.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    timeZone: "UTC",
  });
};

export const RecordExportsPage = () => {
  const orgId = usePrimaryOrganizationId();
  const [loading, setLoading] = useState(false);
  const [jobs, setJobs] = useState<ExportJob[]>([]);
  const [page, setPage] = useState(1);

  useEffect(() => {
    let mounted = true;
    const loadJobs = async () => {
      setLoading(true);
      try {
        const res = await axios.get<ExportJob[]>(
          `${ApiRouteHelpers.organizationExports(orgId)}?limit=100`,
        );
        if (!mounted) return;
        setJobs(Array.isArray(res.data) ? res.data : []);
      } catch {
        if (!mounted) return;
        setJobs([]);
      } finally {
        if (mounted) {
          setLoading(false);
        }
      }
    };
    if (orgId) void loadJobs();
    return () => {
      mounted = false;
    };
  }, [orgId]);

  const sortedJobs = useMemo(
    () =>
      [...jobs].sort(
        (a, b) => new Date(b.requestedAt).getTime() - new Date(a.requestedAt).getTime(),
      ),
    [jobs],
  );
  const totalPages = Math.max(1, Math.ceil(sortedJobs.length / PAGE_SIZE));

  useEffect(() => {
    if (page > totalPages) setPage(totalPages);
  }, [page, totalPages]);

  const pagedJobs = useMemo(() => {
    const start = (page - 1) * PAGE_SIZE;
    return sortedJobs.slice(start, start + PAGE_SIZE);
  }, [page, sortedJobs]);

  return (
    <StandardPageWrapper>
      <div className="tw-boundary">
        <Breadcrumbs
          title="Record Exports"
          source="Privacy Docs"
          sourceRoute={Routes.documents.root}
        />

        {loading ? (
          <p className="text-sm text-neutral-500 mt-4">Loading exports…</p>
        ) : sortedJobs.length === 0 ? (
          <p className="text-sm text-neutral-500 mt-4">
            No exports yet. Generate one from Record Lookup, Consent Records, or GPC Opt-Outs.
          </p>
        ) : (
          <div className="bg-white rounded-lg overflow-hidden border border-gray-200 mt-4">
            <table className="w-full border-separate border-spacing-0 text-sm">
              <thead>
                <tr>
                  <th className="border-b border-gray-200 text-left px-4 py-3 text-xs font-semibold text-neutral-500 uppercase tracking-wide">
                    Status
                  </th>
                  <th className="border-b border-gray-200 text-left px-4 py-3 text-xs font-semibold text-neutral-500 uppercase tracking-wide">
                    Export Type
                  </th>
                  <th className="border-b border-gray-200 text-left px-4 py-3 text-xs font-semibold text-neutral-500 uppercase tracking-wide">
                    Date From
                  </th>
                  <th className="border-b border-gray-200 text-left px-4 py-3 text-xs font-semibold text-neutral-500 uppercase tracking-wide">
                    Date To
                  </th>
                  <th className="border-b border-gray-200 text-left px-4 py-3 text-xs font-semibold text-neutral-500 uppercase tracking-wide">
                    Date Generated
                  </th>
                  <th className="border-b border-gray-200 text-right px-4 py-3" />
                </tr>
              </thead>
              <tbody>
                {pagedJobs.map((job, index) => {
                  const rowBorder =
                    index === pagedJobs.length - 1 ? "" : "border-b border-gray-100";
                  return (
                    <tr key={job.jobId}>
                      <td className={`${rowBorder} px-4 py-3`}>
                        <Pill variant={STATUS_PILL_VARIANT[job.status]} noMargin>
                          {STATUS_LABEL[job.status]}
                        </Pill>
                      </td>
                      <td className={`${rowBorder} px-4 py-3 text-neutral-700`}>
                        {EXPORT_TYPE_LABEL[job.exportType]}
                      </td>
                      <td className={`${rowBorder} px-4 py-3 text-neutral-500`}>
                        {formatDateUtc(job.fromDate)}
                      </td>
                      <td className={`${rowBorder} px-4 py-3 text-neutral-500`}>
                        {formatDateUtc(job.toDate)}
                      </td>
                      <td className={`${rowBorder} px-4 py-3 text-neutral-500`}>
                        {formatDateLocal(job.requestedAt)}
                      </td>
                      <td className={`${rowBorder} px-4 py-3 text-right`}>
                        {job.status === "SUCCEEDED" && job.downloadUrl && (
                          <a
                            href={job.downloadUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-1.5 text-xs font-medium text-purple-700 hover:text-purple-900"
                          >
                            <ArrowDownTrayIcon className="w-4 h-4" />
                            Download CSV
                          </a>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            {sortedJobs.length > PAGE_SIZE && (
              <div className="flex justify-center py-4 border-t border-gray-100">
                <Pagination
                  count={totalPages}
                  page={page}
                  onChange={(_, value) => setPage(value)}
                />
              </div>
            )}
          </div>
        )}
      </div>
    </StandardPageWrapper>
  );
};
